import { AdvertisementTypeEnum, Name } from "@app/types/enums";
import Advertisement from "@components/Advertisement/Advertisement";
import Kicker from "@components/ArticleMeta/Kicker";
import CASBlock from "@components/CASBlock";
import Container from "@components/Container/Container";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import MetaTags from "@components/MetaTags/MetaTags";
import useGenerateGiftURL from "@hooks/useGenerateGiftURL";
import useMostReadArticleList from "@hooks/useMostReadArticleList";
import useShowGiftNofitifcationUpdate from "@hooks/useShowGiftNofitifcationUpdate";
import ArticleAttachmentsFromDrupal from "@pages/Article/components/ArticleAttachmentsFromDrupal";
import ArticleAuthorsList from "@pages/Article/components/ArticleAuthorsList";
import ArticleKeywords, {
  KeywordDisplayOption,
} from "@pages/Article/components/ArticleKeywords";
import ArticleMainImage from "@pages/Article/components/ArticleMainImage/ArticleMainImage";
import ArticleParagraphs from "@pages/Article/components/ArticleParagraphs";
import ArticlePurchaseLink from "@pages/Article/components/ArticlePurchaseLink";
import ArticlesFromSameSection from "@pages/Article/components/ArticlesFromSameSection/ArticlesFromSameSection";
import ArticleSocialSharing from "@pages/Article/components/ArticleSocialSharing";
import ArticleStockSidebar from "@pages/Article/components/ArticleStockSidebar/ArticleStockSidebar";
import ArticleSubscriberLabel from "@pages/Article/components/ArticleSubscriberLabel";
import ArticleSubshare from "@pages/Article/components/ArticleSubshare/ArticleSubshare";
import ArticleSubshareGiftingUpdate from "@pages/Article/components/ArticleSubshare/ArticleSubshareGiftingUpdate";
import ArticleSubshareSent from "@pages/Article/components/ArticleSubshare/ArticleSubshareSent";
import OutBrainWidget from "@pages/Article/components/OutBrainWidget";
import RelatedArticles from "@pages/Article/components/RelatedArticles/RelatedArticles";
import TelegramBanner from "@pages/Article/components/TelegramBanner";
import { ArticleProps } from "@pages/Article/types/Article";
import { checkDisplayPaywall } from "@pages/Article/utils/helpers";
import BreakingNews from "@pages/Section/components/BreakingNews/BreakingNews";
import MostPopular from "@pages/Section/components/MostPopular/MostPopular";
import useOKTAUserStore from "@store/useOKTAUserStore";
import { CDP_PAYWALL_MODULE_ID } from "@util/constant";
import {
  getArticleBlurb,
  getArticleJSONLD,
  getFieldValue,
  getKickerFromArticleDataObject,
  getStockFromArticleDataObject,
  GoogleAdsSlotFactory,
} from "@util/helpers";
import cx from "classnames";
import { find } from "lodash-es";

export default function ArticleDisplayVerticalsNewsDefault({
  article,
  isGiftReceived,
  breakingNews,
  seeAlso,
  sectionNews,
  variant,
  index,
}: ArticleProps): React.ReactElement {
  // De-structure of article so it is cleaner to get the fields.
  const {
    id,
    title,
    updated: publishedDate,
    edited: updatedDate,
    media,
    authors,
    sections,
    tags,
    sanitizedRelatedStories,
    paywall,
    displaySetting,
    embedCodes,
    elements,
    relatedContributorProfile,
    shortUrl,
  } = article;

  const mainSection = sections?.at(0);
  const kicker = getKickerFromArticleDataObject(article);
  const contentAccess = paywall?.contentAccess || "1";
  const displayPaywall = checkDisplayPaywall({
    isGiftReceived,
    variant: variant,
    contentAccess: contentAccess,
  });
  const showRelatedLinksAndNewsletterSignupForm =
    displaySetting?.showRelatedLinksAndNewsletterSignupForm || false;

  const standFirstField =
    find(elements, {
      type: "standfirst",
    })?.fields || [];
  const standFirst = getFieldValue(standFirstField, Name.Standfirst);
  const stockCodes = getStockFromArticleDataObject(article);
  const isPremium = contentAccess === "1";

  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const mostPopularData = useMostReadArticleList();
  const {
    alacrityRes,
    isModalOpen,
    setIsModalOpen,
    handleGenerateGiftURLModal,
  } = useGenerateGiftURL(isPremium);
  const { showGiftNotification, setShowGiftNotification } =
    useShowGiftNofitifcationUpdate();

  return (
    <>
      <ArticleSubshareGiftingUpdate
        isModalOpen={showGiftNotification}
        setIsModalOpen={setShowGiftNotification}
        {...{
          handleGenerateGiftURLModal,
        }}
      />

      <ArticleSubshareSent {...{ alacrityRes, isModalOpen, setIsModalOpen }} />

      <article
        className="display-type--verticalsNewsDefault w-full py-5 font-poppins"
        data-index={index}
        data-cueid={id}
      >
        <Container rootClassName="pt-4">
          <MetaTags
            title={title}
            description={getArticleBlurb(article)}
            keywords={article?.tags?.map((tag) => tag.name).join(", ")}
            slug={article.urlPath}
            shortLink={"/article/" + article.id}
            canonicalUrl={article?.seoFields?.canonicalUrl}
            imageUrl={
              article?.media?.[0]?.content?.fields?.["original-caas"]?.url
            }
            imageAlt={
              article?.media?.[0]?.summary?.find(
                (summaryItem) => summaryItem.key === "alttext"
              )?.value
            }
            jsonLd={getArticleJSONLD(article)}
            robots={"index, follow, max-image-preview:large"}
          />

          <Row>
            <Column
              rootClassName={cx("mx-auto w-full px-3 pb-3", {
                "lg:w-6/12": media && media.length > 0,
              })}
            >
              <>
                {contentAccess === "1" ? (
                  <div className="flex items-center">
                    <ArticleSubscriberLabel isPremium />

                    <ArticleSubshare
                      isPremium={isPremium}
                      userType={OKTAUserInfo?.usertype}
                      {...{
                        handleGenerateGiftURLModal,
                        setIsModalOpen,
                        alacrityRes,
                      }}
                    />
                  </div>
                ) : null}

                {kicker ? <Kicker name={kicker} /> : null}
              </>

              <h1
                data-testid="article-title"
                className="mb-6 font-pt-serif text-8xl font-bold leading-normal text-black md:text-12xl"
              >
                {title}
              </h1>

              <>
                {standFirst ? (
                  <p className="mb-6 mt-2 font-poppins">{standFirst}</p>
                ) : null}
              </>

              <>
                {publishedDate && updatedDate ? (
                  <ArticleAuthorsList
                    rootClassName="mb-6"
                    authors={authors}
                    contributors={relatedContributorProfile}
                    updatedDate={updatedDate}
                    publishedDate={publishedDate}
                    hasUpdatedTimeDisplayed={
                      displaySetting?.hasUpdatedTimeDisplayed
                    }
                  />
                ) : null}
              </>
            </Column>

            <>
              {media ? (
                <Column rootClassName="w-full pb-3 lg:w-6/12 aspect-3x2 h-auto max-h-[17rem] md:max-h-[29rem] lg:max-h-[33rem]">
                  <ArticleMainImage
                    media={media}
                    imgRootClassName="mx-auto h-full"
                    captionRootClassname="p-2 font-poppins text-xs text-gray-550"
                    thumbnailRootClassName="bg-gray-250 aspect-3x2 object-contain max-h-[17rem] md:max-h-[29rem] lg:max-h-[33rem] flex justify-center items-center"
                    landscapeImgRootClassName="w-auto h-auto"
                    invisibleImgRootClass="h-full"
                  />
                </Column>
              ) : null}
            </>

            <Column rootClassName="mx-auto w-full px-3 lg:w-8/12 border-none">
              <div className="mb-6 flex items-center justify-between border-b border-t border-gray-250 py-4">
                {tags ? (
                  <ArticleKeywords
                    keywords={tags}
                    keywordDisplayOption={KeywordDisplayOption.First}
                  />
                ) : null}

                <div className="flex space-x-3">
                  <ArticleSocialSharing
                    articleTitle={title}
                    shareUrl={shortUrl}
                  />

                  <ArticleSubshare
                    isPremium={isPremium}
                    userType={OKTAUserInfo?.usertype}
                    {...{
                      handleGenerateGiftURLModal,
                      setIsModalOpen,
                      alacrityRes,
                    }}
                  />
                </div>
              </div>

              <ArticleAttachmentsFromDrupal
                cueId={article.id}
                rootClassName="mb-6"
              />

              <>
                {elements ? (
                  <ArticleParagraphs
                    id={id}
                    content={elements}
                    seeAlso={seeAlso}
                    embedCodes={embedCodes}
                    displayPaywall={displayPaywall}
                    displayRelatedLinksAndNewsletterSignupForm={
                      showRelatedLinksAndNewsletterSignupForm
                    }
                    rootClassName="text-lg"
                    keywords={tags}
                    sections={sections}
                    index={index}
                  />
                ) : null}
              </>

              <>
                {contentAccess !== "1" ? (
                  <CASBlock cdpId={CDP_PAYWALL_MODULE_ID} index={index} />
                ) : null}
              </>

              <>
                {tags ? (
                  <ArticleKeywords
                    keywords={tags}
                    keywordDisplayOption={KeywordDisplayOption.Rest}
                  />
                ) : null}
              </>

              <RelatedArticles articles={sanitizedRelatedStories} />

              <TelegramBanner />

              <ArticlesFromSameSection data={sectionNews} sections={sections} />
            </Column>

            <Column rootClassName="w-full lg:w-4/12">
              <>
                {mainSection ? (
                  <div className="lg:h-[1050px]">
                    <Advertisement
                      rootClassName="lg:sticky lg:top-[120px] mb-5"
                      adUnitProps={{
                        id:
                          index !== 0 ? `dfp-ad-imu2-${index}` : `dfp-ad-imu2`,
                        type: AdvertisementTypeEnum.IMU2,
                        slot: GoogleAdsSlotFactory.imu2(mainSection.uniqueName),
                        isRefresh: index !== 0,
                      }}
                    />
                  </div>
                ) : null}
              </>

              <ArticleStockSidebar codes={stockCodes} />

              <BreakingNews
                rootClassName="mx-auto mb-5 max-w-[300px]"
                data={breakingNews}
              />

              <div className="mx-auto max-w-[300px]">
                <MostPopular data={mostPopularData} />
              </div>

              <>
                {paywall?.copyright === "SPH" && publishedDate ? (
                  <div className="mx-auto max-w-[300px]">
                    <ArticlePurchaseLink
                      title={title}
                      publishedDate={publishedDate}
                    />
                  </div>
                ) : null}
              </>

              <>
                {mainSection ? (
                  <div className="sticky top-[120px] py-3">
                    <Advertisement
                      adUnitProps={{
                        id:
                          index !== 0 ? `dfp-ad-imu3-${index}` : `dfp-ad-imu3`,
                        type: AdvertisementTypeEnum.IMU3,
                        slot: GoogleAdsSlotFactory.imu3(mainSection.uniqueName),
                        isRefresh: index !== 0,
                      }}
                    />
                  </div>
                ) : null}
              </>
            </Column>

            <>
              {article.urlPath ? (
                <OutBrainWidget
                  articleUrl={article.urlPath}
                  widgetId={index === 0 ? "AR_4" : "AR_5"}
                />
              ) : null}
            </>
          </Row>
        </Container>
      </article>
    </>
  );
}
